import { Axios } from '@utils/api/axios-instance.ts';
import { BaseError, BaseResponse } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import { POST_URL } from '@utils/api/endpoints.ts';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CUSTOM_MODEL_QUERY_KEYS } from './query-keys';

export enum CustomModelDataType {
  TRANSCRIPT = 1,
  TEST = 2,
  AUDIO = 3,
  MANIFEST = 4
}

export interface UpdateModelBody {
  name: string;
  language_id: number;
  organization_id: number;
}

// Legacy QK export for backwards compatibility - will be removed in future
export const QK = {
  customModel: (id: number) => CUSTOM_MODEL_QUERY_KEYS.detail(id),
  customModels: CUSTOM_MODEL_QUERY_KEYS.list,
  customModelsData: (id: number) => CUSTOM_MODEL_QUERY_KEYS.data(id)
};

async function updateModel(
  modelId: number,
  body: UpdateModelBody
): Promise<BaseResponse<unknown>> {
  const url = `${POST_URL(modelId.toString()).updateCustomModel}`;
  const { data } = await Axios.put(url, body);
  return data;
}

export function useUpdateModel() {
  const qc = useQueryClient();
  const { t } = useTranslation();

  return useMutation<
    BaseResponse<unknown>,
    AxiosError<BaseError>,
    { modelId: number; body: UpdateModelBody }
  >(({ modelId, body }) => updateModel(modelId, body), {
    onMutate: async ({ modelId, body }) => {
      await qc.cancelQueries(CUSTOM_MODEL_QUERY_KEYS.detail(modelId));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const prev = qc.getQueryData<any>(
        CUSTOM_MODEL_QUERY_KEYS.detail(modelId)
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      qc.setQueryData(CUSTOM_MODEL_QUERY_KEYS.detail(modelId), (old: any) =>
        old ? { ...old, result: { ...old.result, name: body.name } } : old
      );
      return { prev, modelId };
    },
    onSuccess: (_data, { modelId }) => {
      // Invalidate specific model detail
      qc.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.detail(modelId));
      // Invalidate all list queries to refresh the updated model
      qc.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.list);
      // Invalidate model data queries
      qc.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.data(modelId));
    },
    onError: error => {
      const fallback = t('an-error-occurred-while-updating-custom-model');
      const data = error.response?.data;
      const messages = Array.isArray(data?.detail)
        ? data.detail.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (err: any) => `${err.loc?.[1] ?? 'field'}: ${err.msg}`
          )
        : [data?.message || data?.error_description || fallback];
      toast.error(messages.join('\n'));
    },
    useErrorBoundary: false
  });
}
